import Card from "common/Boxes/Card.tsx";
import Flex from "common/Boxes/Flex.tsx";
import IconWithFallback from "Global/IconWithFallback.tsx";
import {
  ICON_TYPES,
  MARGIN_TYPE_TO_DISPLAY,
} from "@shared/common/constants.ts";
import AutoSizeText from "common/Text/AutoSizeText.tsx";
import Tag from "common/Tags/Tag.tsx";
import { hexToRGB } from "@shared/utils/misc.ts";
import React, { useContext } from "react";
import { ConsolidatedCoinDataWithKey, LanguageNameSpaces } from "@shared/types";
import styled, { css, useTheme } from "styled-components";
import { Pill } from "common/NewLook";
import CoinActionsDropdown from "features/portfolio/common/CoinActionsDropdown.tsx";
import { Divider } from "common/styles.ts";
import TextWithIcon from "common/Text/TextWithIcon.tsx";
import Row from "antd/es/row";
import Col from "antd/es/col";
import Text from "common/Typography/Text.tsx";
import { typography } from "@shared/common/Themes/typography.ts";
import { DrawerContext } from "common/Drawers/Drawer.context.ts";
import usePositionItem from "features/portfolio/common/usePositionItem.tsx";
import NewLookPositionCard from "features/portfolio/NewLook/NewLookPositionCard.tsx";
import { MOBILE_SEARCH } from "common/constants.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import {
  useSubUserPermissions,
  useSymbolsMetadata,
} from "@shared/utils/hooks/misc.ts";

interface PositionCardProps {
  coinData: ConsolidatedCoinDataWithKey;
}

const PositionCard = ({ coinData }: PositionCardProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const theme = useTheme();
  const { isDrawer } = useContext(DrawerContext) || {};
  const { getMainCoin } = useSymbolsMetadata();
  const {
    getPrice,
    getPriceChange,
    openCoinExtraDetails,
    getMargin,
    getPositionSize,
    getUnrealizedPnl,
    getEntryPrice,
    getLiquidationPrice,
    showEditLeverage,
    getROI,
  } = usePositionItem({});
  const suffix = getMainCoin(coinData.symbol, coinData.exchanges[0]);
  const { isReadOnly } = useSubUserPermissions();

  if (theme.isNewLook) return <NewLookPositionCard coinData={coinData} />;

  const tags = theme.isNewLook ? (
    <Flex $justifyContent="flex-start" style={{ columnGap: 4, marginTop: 4 }}>
      <Pill tone={coinData.is_short ? "red" : "green"}>
        {coinData.is_short ? t("positions.short") : t("positions.long")}
      </Pill>
      <Pill>
        {MARGIN_TYPE_TO_DISPLAY[coinData.margin_type]} {coinData.leverage}x
      </Pill>
    </Flex>
  ) : (
    <Flex $justifyContent="flex-start" style={{ columnGap: 4 }}>
      <Tag
        color={coinData.is_short ? theme.colors.red : theme.colors.green}
        style={{ whiteSpace: "nowrap" }}
      >
        {coinData.is_short ? t("positions.short") : t("positions.long")}
      </Tag>
      <Tag
        color={theme.colors.primaryGrey}
        bgColor={hexToRGB(theme.colors.secondaryGrey, 0.1)}
        style={{ whiteSpace: "nowrap" }}
      >
        {MARGIN_TYPE_TO_DISPLAY[coinData.margin_type]} {coinData.leverage}x
      </Tag>
    </Flex>
  );

  return (
    <StyledCard
      $isDrawer={isDrawer}
      onClick={() => openCoinExtraDetails(coinData)}
    >
      <div>
        <Flex>
          <Flex $justifyContent="flex-start">
            <IconWithFallback
              iconType={ICON_TYPES.COIN}
              coinOrExchangeName={coinData.symbol}
              pxIconSize={32}
            />
            <SymbolTagsWrapper>
              <AutoSizeText>{coinData.symbol}</AutoSizeText>
              {tags}
            </SymbolTagsWrapper>
          </Flex>
          <ChartActionsWrapper>
            <ChartWrapper>{getPriceChange(coinData)}</ChartWrapper>
            {!isDrawer && !isReadOnly && (
              <CoinActionsDropdown
                coinData={coinData}
                isPosition={true}
                showEditLeverage={showEditLeverage(coinData)}
              />
            )}
          </ChartActionsWrapper>
        </Flex>
        <StyledDivider />
        <StyledRow gutter={12}>
          <Item
            title={t("positions.size")}
            value={getPositionSize(coinData, true)}
            span={COLUMNS_SPANS[0]}
            suffix={suffix}
          />
          <Item
            title={t("positions.margin")}
            value={getMargin(coinData, true, true)}
            span={COLUMNS_SPANS[1]}
            suffix={suffix}
          />
          <Item
            title={t("positions.upnl")}
            value={
              <PnlWrapper>
                {getUnrealizedPnl(coinData, true)} {getROI(coinData)}
              </PnlWrapper>
            }
            span={COLUMNS_SPANS[2]}
            isLast={true}
            suffix={suffix}
          />
        </StyledRow>
      </div>
      <div>
        <Row gutter={12}>
          <Item
            title={t("positions.entry_price")}
            value={getEntryPrice(coinData)}
            span={COLUMNS_SPANS[0]}
          />
          <Item
            title={t("positions.price")}
            value={getPrice(coinData)}
            span={COLUMNS_SPANS[1]}
          />
          <Item
            title={t("positions.liq_price")}
            value={getLiquidationPrice(coinData)}
            span={COLUMNS_SPANS[2]}
            isLast={true}
          />
        </Row>
        {!isDrawer && (
          <>
            <StyledDivider />
            <TextWithIcon
              text={coinData.clients[0]}
              iconType={ICON_TYPES.EXCHANGE}
              coinOrExchangeName={coinData.exchanges[0]}
              textStyle={{ color: theme.colors.primaryText }}
            />
          </>
        )}
      </div>
    </StyledCard>
  );
};

export default React.memo(PositionCard);

const COLUMNS_SPANS = [9, 7, 8];

const StyledCard = styled(Card)<{ $isDrawer?: boolean }>`
  padding: 12px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: ${(p) => (p.$isDrawer ? 218 : 268)}px;
  @media (max-width: ${MOBILE_SEARCH}) {
    height: auto !important;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      height: auto;
      padding: 16px;
      justify-content: flex-start;
      gap: 12px;
      @media (max-width: 600px) {
        padding: 12px;
        gap: 6px;
      }
      transition: box-shadow 0.15s;
      &:hover {
        box-shadow: inset 0 0 0 1px ${p.theme.colors.lightBorderGrey},
          ${p.theme.shadows.card};
      }
    `}
`;
const StyledDivider = styled(Divider)`
  margin: 12px 0;
  ${(p) =>
    p.theme.isNewLook &&
    `margin: 16px 0; @media (max-width: 600px) { margin: 6px 0; }`}
`;
const PnlWrapper = styled(Flex)`
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 4px;
`;
const ChartWrapper = styled.div`
  max-width: 125px;
  min-width: 0;
  flex: 1;
  padding-left: 8px;
`;
const ChartActionsWrapper = styled(Flex)`
  flex: 1;
  min-width: 0;
  justify-content: flex-end;
`;
const SymbolTagsWrapper = styled.div`
  margin-left: 12px;
`;
// New look: with the card's 12px gap, the two rows of figures sit 16px
// apart, the same as the space around the dividers.
const StyledRow = styled(Row)`
  margin-bottom: 12px;
  ${(p) => p.theme.isNewLook && `margin-bottom: 4px;`}
`;

interface ItemProps {
  title: string;
  value: React.JSX.Element;
  span: number;
  isLast?: boolean;
  suffix?: string;
}

const Item = ({ title, value, span, isLast, suffix }: ItemProps) => {
  return (
    <Col
      key={title}
      span={span}
      style={{ textAlign: isLast ? "end" : "start" }}
    >
      <Text type="secondary" $size={typography.fontSizes.S} as="div">
        {title} {!!suffix && `(${suffix})`}
      </Text>
      <Text as="div">{value}</Text>
    </Col>
  );
};
